import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SubsWall, { STYLE_PALETTES, type SubTile, type TileStyle } from "../components/SubsWall";
import "./WallEmbed.css";

interface ApiTile {
  id: string;
  name: string;
  message: string;
  style: TileStyle;
  color: string;
  x_pct: number;
  y_pct: number;
  monthly_cents: number;
  founder_number: number | null;
  anniversary_months: number;
  fade_started_at: number | null;
}
interface ApiResp {
  source: "mock" | "live";
  creator?: { handle: string; display_name: string };
  tiles?: ApiTile[];
}

function apiTileToSubTile(t: ApiTile): SubTile {
  return {
    id: t.id,
    name: t.name,
    message: t.message,
    style: t.style,
    color: t.color,
    xPct: t.x_pct,
    yPct: t.y_pct,
    monthlyCents: t.monthly_cents,
    anniversaryMonths: t.anniversary_months,
    founderNumber: t.founder_number,
    boostUntil: null,
    fadeStartedAt: t.fade_started_at,
    badge: null,
    blink: t.style === "neon" && t.monthly_cents >= 1000,
  };
}

// Mock fallback so /demo-creator/embed renders the design demo perfectly.
const DEMO_SEED: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle }> = [
  { name: "Sarah K.",     message: "first one in. shipping forever.",         monthlyCents: 500,   style: "spray" },
  { name: "Marcus T.",    message: "$5/mo for a forever spot.",               monthlyCents: 500,   style: "chalk" },
  { name: "priya.n",      message: "tell my mom I made it on the wall",       monthlyCents: 500,   style: "marker" },
  { name: "lofi.ghost",   message: "playing your VOD on loop while I code",   monthlyCents: 1000,  style: "neon", blink: true },
  { name: "Maya Chen",    message: "the soundtrack saved my run",             monthlyCents: 2500,  style: "neon", blink: true, anniversaryMonths: 7 },
  { name: "Hiro Tanaka",  message: "watching since the API days",             monthlyCents: 5000,  style: "carved", badge: "shield" },
  { name: "M.J. Asante",  message: "yes I bought the biggest tile. fight me.",monthlyCents: 10000, style: "neon", blink: true, badge: "crown" },
  { name: "Helena Voss",  message: "day-one founder. forever.",               monthlyCents: 25000, style: "neon", blink: true, badge: "star", founderNumber: 1, anniversaryMonths: 12 },
];

function buildMock(): SubTile[] {
  return DEMO_SEED.map((s, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3) % 3;
    return {
      id: `e${i}`,
      name: s.name,
      message: s.message,
      style: s.style,
      color: STYLE_PALETTES[s.style][(i + 2) % 8],
      xPct: 24 + col * 26 + ((i * 7) % 5) - 2,
      yPct: 22 + row * 28 + ((i * 11) % 5) - 2,
      monthlyCents: s.monthlyCents,
      anniversaryMonths: s.anniversaryMonths ?? 0,
      founderNumber: s.founderNumber ?? null,
      boostUntil: null,
      fadeStartedAt: null,
      badge: s.badge ?? null,
      blink: s.blink ?? false,
    };
  });
}

// Headless iframe-friendly wall — no nav, no footer, no chrome. Picks up
// the same data as /:handle but renders only the canvas + a thin attribution
// strip. Re-fetches every 60s so embeds stay live without a page refresh.
export default function WallEmbed(): JSX.Element {
  const { handle = "demo" } = useParams<{ handle: string }>();
  const [tiles, setTiles] = useState<SubTile[]>(buildMock);

  useEffect(() => {
    let cancelled = false;
    const load = (): void => {
      fetch(`/api/wall/${encodeURIComponent(handle)}`, { headers: { Accept: "application/json" } })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: ApiResp | null) => {
          if (cancelled || !j) return;
          if (j.source === "live" && j.tiles) {
            setTiles(j.tiles.map(apiTileToSubTile));
          }
        })
        .catch(() => {});
    };
    load();
    const t = window.setInterval(load, 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [handle]);

  return (
    <div className="we-page">
      <div className="we-frame">
        <SubsWall tiles={tiles} theme="neon" aspectRatio={16 / 9} autoGrow />
      </div>
      <a className="we-attrib" href={`https://subswall.com/${handle}`} target="_blank" rel="noopener noreferrer">
        <span className="we-glyph"></span> Powered by SubsWall · subswall.com/{handle}
      </a>
    </div>
  );
}
